/**
 * HTML per i micro-pulsanti azione in tabella/card (tema Blu).
 */

import { normalizeBand } from '../main/studioLabels.js';
import { formatPriceBandLabel } from '../main/studioPriceBands.js';
import { escapeHtml } from '../utils/escapeHtml.js';

/** Chip assegnazione asta: 3 lettere nome + prezzo a 3 cifre (larghezza fissa tabella). */
export function formatPresoChipParts(ownerName, price) {
  const name = String(ownerName || '').trim();
  const shortName = name.slice(0, 3);
  const priceNum = Number(price);
  const priceStr = Number.isFinite(priceNum) && priceNum >= 1
    ? String(Math.min(999, Math.round(priceNum))).padStart(3, '0')
    : '---';
  const ariaLabel = name ? `${name} · ${priceNum}` : String(priceNum);
  return { shortName, priceStr, ariaLabel };
}

function renderPresoChipLabel(ownerName, price) {
  const { shortName, priceStr, ariaLabel } = formatPresoChipParts(ownerName, price);
  return {
    ariaLabel,
    html: `<span class="preso-exit-chip-label" aria-hidden="true"><span class="preso-chip-name">${escapeHtml(shortName)}</span><span class="preso-chip-sep">·</span><span class="preso-chip-price num">${priceStr}</span></span>`,
  };
}

/** Icona «Preso da me» — scudo/casa mia (più chiaro a 12px) */
export const ICON_PRESO_ME = `<svg class="action-icon action-icon--preso-me" viewBox="0 0 16 16" width="12" height="12" aria-hidden="true" focusable="false"><path fill="currentColor" d="M8 1.6 2.4 6.2v7.7h4.1V9.6h3V13.9h4.1V6.2L8 1.6Zm0 1.55 4.45 3.55v6.05h-1.8V8.45H5.35v4.3H3.55V6.7L8 3.15Z"/></svg>`;

/** Icona «Preso da altri» — due persone (rosa avversaria) */
export const ICON_PRESO_ALTRI = `<svg class="action-icon action-icon--preso-altri" viewBox="0 0 16 16" width="12" height="12" aria-hidden="true" focusable="false"><circle cx="5.6" cy="4.4" r="2.1" fill="currentColor"/><path fill="currentColor" d="M1.7 13.1c0-1.85 1.75-3.15 3.9-3.15s3.9 1.3 3.9 3.15v.55H1.7v-.55Z"/><circle cx="11.1" cy="5.1" r="1.85" fill="currentColor"/><path fill="currentColor" d="M8.35 13.1c.35-1.35 1.55-2.35 3.1-2.35 1.7 0 3 1.1 3 2.5v.35H8.55c-.08-.15-.14-.32-.2-.5Z"/></svg>`;

/** Icona «Segna preso» — martello asta */
export const ICON_PRESO_ASSIGN = `<svg class="action-icon action-icon--preso-assign" viewBox="0 0 16 16" width="12" height="12" aria-hidden="true" focusable="false"><path fill="currentColor" d="M9.05 1.6 7.3 6.45l1.35.45 1.75-4.85-1.35-.45ZM3.75 10.85l7.6 2.55.55-1.65-7.6-2.55-.55 1.65Z"/><path fill="currentColor" d="M2.45 13.2h11v1.5h-11v-1.5Z"/></svg>`;

/** Icona «+ Studio» — lista target + plus */
export const ICON_STUDIO_ADD = `<svg class="action-icon action-icon--studio-add" viewBox="0 0 16 16" width="12" height="12" aria-hidden="true" focusable="false"><path fill="currentColor" d="M3.25 1.75h6.2l2.3 2.3v10.2H3.25V1.75zm1.15 1.15v10.2h6.2V4.55H8.9V2.9H4.4zm5.65.55v.9h.9l-.9-.9zM5.4 6.4h5.2v1.1H5.4V6.4zm0 2.4h3.4v1.1H5.4V8.8z"/><path fill="currentColor" d="M11.35 10.35h1.1v1.35h1.35v1.1H12.45v1.35h-1.1V12.8H9.95v-1.1h1.4v-1.35z"/></svg>`;

/** Icona «In Studio» — lista target + check */
export const ICON_STUDIO_IN = `<svg class="action-icon action-icon--studio-in" viewBox="0 0 16 16" width="12" height="12" aria-hidden="true" focusable="false"><path fill="currentColor" d="M3.25 1.75h6.2l2.3 2.3v10.2H3.25V1.75zm1.15 1.15v10.2h6.2V4.55H8.9V2.9H4.4zm5.65.55v.9h.9l-.9-.9zM5.4 6.4h5.2v1.1H5.4V6.4zm0 2.4h2.6v1.1H5.4V8.8z"/><path fill="currentColor" fill-rule="evenodd" d="m8.55 12.85 3.4-3.4.85.85-4.25 4.25-2.2-2.2.85-.85 1.35 1.35z"/></svg>`;

/** Etichetta/tag giocatore */
export const ICON_TAG = `<svg class="studio-action-icon studio-tag-icon" viewBox="0 0 16 16" width="12" height="12" aria-hidden="true" focusable="false"><path fill="currentColor" d="M2.2 2.2h5.05c.28 0 .55.11.75.31l5.5 5.5a1.05 1.05 0 0 1 0 1.48l-3.91 3.91a1.05 1.05 0 0 1-1.48 0l-5.5-5.5a1.05 1.05 0 0 1-.31-.75V3.25c0-.58.47-1.05 1.05-1.05Zm2.1 3.35a1.25 1.25 0 1 0 0-2.5 1.25 1.25 0 0 0 0 2.5Z"/></svg>`;

/** Slot Studio (vuoto) — tre corsie/righe */
export const ICON_SLOT = `<svg class="studio-action-icon studio-slot-icon" viewBox="0 0 16 16" width="12" height="12" aria-hidden="true" focusable="false"><path fill="currentColor" d="M2.5 2.75h11v2.1H2.5zm0 4.2h11v2.1H2.5zm0 4.2h11v2.1H2.5z"/></svg>`;

/** Storico stagioni — barre stats (centrate otticamente nel 16×16) */
export const ICON_HISTORY = `<svg class="studio-action-icon studio-history-icon" viewBox="0 0 16 16" width="12" height="12" aria-hidden="true" focusable="false"><path fill="currentColor" d="M2.75 11.35h10.5v1.15H2.75zm1.35-1V7.45h1.9v2.9zm3.1 0V3.7h1.9v6.65zm3.1 0V5.95h1.9v4.4z"/></svg>`;

/** Confronto giocatori — due colonne affiancate */
export const ICON_COMPARE = `<svg class="studio-action-icon studio-compare-icon" viewBox="0 0 16 16" width="12" height="12" aria-hidden="true" focusable="false"><path fill="currentColor" d="M2.75 2.75h3.6v10.5H2.75zm7.15 0H13.5v10.5H9.9zM5.35 4.5H4.1v7h1.25v-7zm7.15 0h-1.25v7H12.5v-7z"/></svg>`;

/** Rimuovi da Studio / svuota bozza */
export const ICON_REMOVE = `<svg class="studio-action-icon studio-remove-icon" viewBox="0 0 16 16" width="12" height="12" aria-hidden="true" focusable="false"><path fill="currentColor" d="M6.15 2.35h3.7l.35.9h2.95v1.25H2.85V3.25h2.95l.35-.9ZM4.6 5.6h1.25v6.55H4.6V5.6Zm2.55 0h1.25v6.55H7.15V5.6Zm2.55 0H11v6.55H9.7V5.6ZM3.85 13.4c0 .55.45 1 1 1h6.3c.55 0 1-.45 1-1V5.1H3.85v8.3Z"/></svg>`;

/** Conferma / salva modifica */
export const ICON_SAVE_CHECK = `<svg class="studio-action-icon studio-save-icon" viewBox="0 0 16 16" width="12" height="12" aria-hidden="true" focusable="false"><path fill="currentColor" fill-rule="evenodd" d="m6.35 11.85 5.5-5.5-.9-.9-4.6 4.6-2.1-2.1-.9.9 3.5 3.5z"/></svg>`;

/** Annulla uscito (preso da altri) */
export const ICON_UNDO_EXIT = `<svg class="action-icon action-icon--undo-exit" viewBox="0 0 16 16" width="12" height="12" aria-hidden="true" focusable="false"><path fill="currentColor" d="M3.25 8.25a4.75 4.75 0 0 1 7.9-3.55l.55-.55v-1.4h1.55v3.75H9.5V5.5l-.45.45a3.25 3.25 0 1 0 1.15 2.55h1.35a4.6 4.6 0 1 1-1.7-3.55l.55-.55H7.7V2.75h3.75v3.75H9.7l-.55-.55A4.75 4.75 0 0 0 3.25 8.25Z"/></svg>`;

export function renderUndoExitButton({
  playerKey = '',
  name = '',
  team = '',
  compact = false,
} = {}) {
  const cls = compact
    ? 'studio-undo-exit-btn'
    : 'action-btn preso-btn preso-btn--undo-exit';
  return `
    <button type="button" class="${cls}"
            data-action="undo-exit"
            data-player-name="${name}"
            data-player-team="${team}"
            data-player-key="${playerKey}"
            title="Annulla uscito — rimetti in lista"
            aria-label="Annulla uscito"
            data-testid="undo-exit-btn">
      ${ICON_UNDO_EXIT}
    </button>`;
}

export function renderStudioListTargetBadge(target) {
  if (!target) return "";
  const priceNum = Number(target.maxPrice);
  if (!(priceNum > 0)) return "";
  return `
    <span class="studio-list-target" data-testid="studio-list-target">
      <span class="studio-list-target-price num" title="Prezzo massimo">${priceNum}M</span>
    </span>`;
}

/** Azioni colonna ASTA (live): due tasti se libero; un solo chip se già assegnato. */
export function renderLiveAstaActions({
  playerKey = "",
  name = "",
  team = "",
  inRoster = false,
  exited = false,
  exitHasValidPrice = false,
  exitPrice = 0,
  exitOpponentLabel = "",
  assignedExitMode = false,
  rosterPrice = 0,
} = {}) {
  // Assegnato: un solo chip al posto dei due tasti (click → form modifica + annulla)
  if (inRoster) {
    const chip = renderPresoChipLabel('Io', rosterPrice);
    return `
    <div class="studio-list-actions studio-list-actions--live" data-testid="live-quick-actions">
      <button type="button" class="action-btn preso-btn preso-btn--assigned preso-btn--assigned-me"
              data-action="edit-assignment"
              data-assignment-side="me"
              data-player-name="${name}"
              data-player-team="${team}"
              data-player-key="${playerKey}"
              title="Modifica prezzo o annulla assegnazione"
              aria-label="Assegnato a te ${chip.ariaLabel}"
              data-testid="edit-assignment-me-btn">
        ${chip.html}
      </button>
    </div>`;
  }

  if (exited) {
    const ownerName = assignedExitMode && exitOpponentLabel ? exitOpponentLabel : '';
    const chip = renderPresoChipLabel(ownerName, exitPrice);
    return `
    <div class="studio-list-actions studio-list-actions--live" data-testid="live-quick-actions">
      <button type="button" class="action-btn preso-btn preso-btn--assigned preso-btn--assigned-others"
              data-action="edit-assignment"
              data-assignment-side="others"
              data-player-name="${name}"
              data-player-team="${team}"
              data-player-key="${playerKey}"
              title="Modifica uscita o annulla assegnazione"
              aria-label="Assegnato ${chip.ariaLabel}"
              data-testid="edit-assignment-others-btn">
        ${chip.html}
      </button>
    </div>`;
  }

  // Libero: un solo tasto → form assegnazione (default Io)
  return `
    <div class="studio-list-actions studio-list-actions--live" data-testid="live-quick-actions">
      <button type="button" class="action-btn preso-btn preso-btn--assign"
              data-action="preso"
              data-player-name="${name}"
              data-player-team="${team}"
              data-player-key="${playerKey}"
              title="Segna preso — prezzo e rosa (puoi cambiare chi)"
              aria-label="Segna preso"
              data-testid="preso-assign-btn">
        ${ICON_PRESO_ASSIGN}
      </button>
    </div>`;
}

/**
 * Controlli Studio in sola lettura (live), valori censurati fino a hover riga.
 */
export function renderStudioReadonlyActions(seed = {}) {
  const maxPrice = Number(seed.maxPrice) || 0;
  const priceText = formatPriceBandLabel(maxPrice, { empty: '€' });
  const hasBand = seed.band != null && String(seed.band).trim() !== '';
  const band = hasBand ? normalizeBand(seed.band) : null;
  const slotText = band || '·';
  const notes = String(seed.notes || '');
  const labels = Array.isArray(seed.labels) ? seed.labels : [];
  const tagLabel = labels.length ? String(labels.length) : ICON_TAG;

  return `
    <div class="studio-list-actions studio-list-actions--readonly" data-testid="studio-list-readonly" aria-label="Obiettivo Studio (sola lettura)">
      <span class="studio-ro-chip studio-ro-price${maxPrice > 0 ? '' : ' is-empty'}" title="Prezzo max">
        <span class="studio-censor"><span class="studio-censor-val num">${priceText}</span></span>
      </span>
      <span class="studio-ro-chip studio-ro-slot" title="Slot">
        <span class="studio-censor"><span class="studio-censor-val">${slotText}</span></span>
        ${notes ? '<span class="studio-row-meta-dot" aria-hidden="true"></span>' : ''}
      </span>
      <span class="studio-ro-chip studio-ro-tags" title="${labels.length ? labels.join(', ') : 'Tag'}">
        <span class="studio-censor"><span class="studio-censor-val studio-censor-val--icon">${tagLabel}</span></span>
      </span>
    </div>`;
}

/**
 * Azioni colonna AZIONI (prep): shell per i 4 controlli rapidi (montati da sync).
 */
export function renderStudioListActions({
  playerKey = "",
  name = "",
  team = "",
  inStudio = false,
  target,
  inRoster = false,
  exited = false,
  exitHasValidPrice = false,
  auctionMode = "prep",
} = {}) {
  if (auctionMode === "live") {
    return renderLiveAstaActions({
      playerKey,
      name,
      team,
      inRoster,
      exited,
      exitHasValidPrice,
    });
  }

  return `
    <div class="studio-list-actions studio-list-actions--quick"
         data-testid="studio-list-quick"
         data-player-key="${playerKey}"
         data-player-name="${name}"
         data-player-team="${team}"></div>`;
}

/** Alias per import esistenti */
export function renderStudioActionButton(options) {
  return renderStudioListActions(options);
}
